/**
 * Browser Notifications utility
 * Handles requesting permission and showing browser notifications
 */

let permissionGranted = false;

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    permissionGranted = true;
    return true;
  }

  if (Notification.permission === 'denied') {
    console.warn('Notification permission denied');
    return false;
  }

  // Request permission
  const permission = await Notification.requestPermission();
  permissionGranted = permission === 'granted';
  return permissionGranted;
};

interface NotificationOptions {
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  link?: string;
  body?: string;
  data?: any;
}

/**
 * Show a browser notification
 */
export const showBrowserNotification = (
  title: string,
  options: NotificationOptions = {}
): Notification | null => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }

  const defaultOptions: NotificationOptions = {
    icon: '/logo192.png', // Default icon
    badge: '/logo192.png',
    tag: 'st-cael-notification',
    requireInteraction: false,
    ...options,
  };

  const notification = new Notification(title, {
    icon: defaultOptions.icon,
    badge: defaultOptions.badge,
    tag: defaultOptions.tag,
    requireInteraction: defaultOptions.requireInteraction,
    body: defaultOptions.body,
    data: defaultOptions.data,
  });

  // Auto-close after 5 seconds if not requiring interaction
  if (!defaultOptions.requireInteraction) {
    setTimeout(() => {
      notification.close();
    }, 5000);
  }

  // Handle click
  notification.onclick = (event) => {
    event.preventDefault();
    window.focus();
    
    // Navigate if link is provided
    if (options.link) {
      window.location.href = options.link;
    }
    
    notification.close();
  };

  return notification;
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = (): boolean => {
  return 'Notification' in window;
};

/**
 * Check if permission is granted
 */
export const hasNotificationPermission = (): boolean => {
  return 'Notification' in window && Notification.permission === 'granted';
};

/**
 * Get current permission status
 */
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

